import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useUserMsp } from "../hooks/useUserMsp";
import { useIntegration } from "../hooks/useIntegration";
import { canManageTheoUsers, isHorizonRootRole } from "../utils/horizonRoles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LayeredAutocompleteSelect from "../components/LayeredAutocompleteSelect";
import { getDropdownSafeDialogSx } from "../utils/dialogLayering";
import IntegrationApi from "../apis/integrationApi";

/** Role values persisted in `horizondb.user.role` (Theo Users form; keyword: theo user role select). */
type TheoMgmtRole = "ADMIN" | "TECHNICIAN" | "ROOT";

const BASE_THEO_ROLE_OPTIONS: { value: Exclude<TheoMgmtRole, "ROOT">; label: string }[] = [
  { value: "ADMIN", label: "ADMIN" },
  { value: "TECHNICIAN", label: "TECHNICIAN" },
];

const integrationApi = new IntegrationApi();

const ENTRA_USERS_URL = "https://entra.microsoft.com/#view/Microsoft_AAD_UsersAndTenants/UserManagementMenuBlade/~/AllUsers";

function teamsStatusLabel(status?: string): string {
  if (status === "connected") return "Connected";
  if (status === "linked") return "Linked";
  return "Not connected";
}

function teamsStatusColor(status?: string): string {
  if (status === "connected") return "#067647";
  if (status === "linked") return "#B54708";
  return "#838383";
}

function teamsStatusHint(status?: string): string {
  if (status === "connected") return "They have messaged Theo in Teams.";
  if (status === "linked") return "Object ID saved. They still need to message Theo once in Teams.";
  return "No Teams chat yet, and no Entra object ID is saved.";
}

const TheoUsers: React.FC = () => {
  const {
    userMspInfo,
    isLoading: userMspLoading,
    error: userMspError,
  } = useUserMsp();
  const {
    users,
    isLoading,
    error,
    getUsersByMspId,
    createUser,
    updateUser,
    deleteUser,
  } = useIntegration();

  // State for dialogs and forms
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [formData, setFormData] = useState({
    email: '',
    role: 'TECHNICIAN' as TheoMgmtRole,
    psa_user_id: '',
    entra_object_id: '',
  });
  const [psaUsers, setPsaUsers] = useState<Array<{ id: string; name: string; email?: string }>>([]);
  const [loadingPsaUsers, setLoadingPsaUsers] = useState(false);
  const [isHaloOnlyEnabledPsa, setIsHaloOnlyEnabledPsa] = useState(false);
  const showPsaUserPicker = isHaloOnlyEnabledPsa && (loadingPsaUsers || psaUsers.length > 0);
  const [createError, setCreateError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const mayManageUsers = canManageTheoUsers(userMspInfo?.role);
  const mayAssignRoot = isHorizonRootRole(userMspInfo?.role);
  const roleSelectOptions: { value: TheoMgmtRole; label: string }[] = mayAssignRoot
    ? [...BASE_THEO_ROLE_OPTIONS, { value: "ROOT", label: "ROOT" }]
    : [...BASE_THEO_ROLE_OPTIONS];

  useEffect(() => {
    if (userMspInfo?.msp_id) {
      getUsersByMspId(userMspInfo.msp_id.toString());
    }
  }, [getUsersByMspId, userMspInfo?.msp_id]);

  useEffect(() => {
    if (!userMspInfo?.msp_id) {
      setIsHaloOnlyEnabledPsa(false);
      return;
    }

    const fetchPsaPlatformState = async () => {
      try {
        const response = await integrationApi.getPlatforms(String(userMspInfo.msp_id));
        const allPlatforms = response?.rows || response || [];
        const enabledPsaPlatforms = (Array.isArray(allPlatforms) ? allPlatforms : [])
          .filter((platform: any) => String(platform?.type || '').toLowerCase() === 'psa' && Number(platform?.enabled) === 1);
        const hasOnlyHaloEnabled =
          enabledPsaPlatforms.length === 1 &&
          String(enabledPsaPlatforms[0]?.name || '').toLowerCase() === 'halo';
        setIsHaloOnlyEnabledPsa(hasOnlyHaloEnabled);
        if (!hasOnlyHaloEnabled) {
          setPsaUsers([]);
          setFormData((prev) => ({ ...prev, psa_user_id: '' }));
        }
      } catch (platformError) {
        console.error('Failed to load platform state for PSA user picker:', platformError);
        setIsHaloOnlyEnabledPsa(false);
      }
    };

    fetchPsaPlatformState();
  }, [userMspInfo?.msp_id]);

  useEffect(() => {
    if (!isHaloOnlyEnabledPsa || !isCreateDialogOpen || !userMspInfo?.msp_id || loadingPsaUsers) {
      return;
    }

    if (psaUsers.length > 0) {
      return;
    }

    const fetchPsaUsers = async () => {
      setLoadingPsaUsers(true);
      try {
        const response = await integrationApi.getPsaUsersByMspId(String(userMspInfo.msp_id));
        const users = Array.isArray(response) ? response : [];
        const validUsers = users
          .filter((user: any) => user?.id && user?.name)
          .map((user: any) => ({
            id: String(user.id),
            name: String(user.name),
            email: user.email ? String(user.email) : "",
          }));
        setPsaUsers(validUsers);
      } catch (fetchError) {
        console.error("Failed to fetch PSA users:", fetchError);
      } finally {
        setLoadingPsaUsers(false);
      }
    };

    fetchPsaUsers();
  }, [isHaloOnlyEnabledPsa, isCreateDialogOpen, loadingPsaUsers, psaUsers.length, userMspInfo?.msp_id]);

  // Removed global unhandledrejection handler to avoid dev overlay

  //

  // Handle create user
  const handleCreateUser = async () => {
    if (!userMspInfo?.msp_id) return;
    
    setCreateError(null);
    setIsCreating(true);
    
    const userData = {
      ...formData,
      msp_id: userMspInfo.msp_id
    };
    
    
    try {
      // Call the Redux action and await the result
      await createUser(userData);
      setIsCreateDialogOpen(false);
      setFormData({ email: '', role: 'TECHNICIAN', psa_user_id: '', entra_object_id: '' });
      setPsaUsers([]);
      setCreateError(null);
      // Refresh the users list to show the updated data
      if (userMspInfo?.msp_id) {
        getUsersByMspId(userMspInfo.msp_id.toString());
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      
      // Extract the error message from the error object
      let errorMessage = 'Failed to create user';
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      setCreateError(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  // Handle edit user
  const handleEditUser = async () => {
    if (!selectedUser) return;
    
    setEditError(null);
    setIsEditing(true);
    
    try {
      const result = await updateUser(selectedUser.id.toString(), {
        role: formData.role,
        entra_object_id: formData.entra_object_id.trim(),
      });
      
      // Only proceed if the action was successful
      if (result) {
        setIsEditDialogOpen(false);
        setSelectedUser(null);
        setFormData({ email: '', role: 'TECHNICIAN', psa_user_id: '', entra_object_id: '' });
        setEditError(null);
        // Refresh the users list to show the updated data
        if (userMspInfo?.msp_id) {
          getUsersByMspId(userMspInfo.msp_id.toString());
        }
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      
      // Extract the error message from the error object
      let errorMessage = 'Failed to update user';
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      setEditError(errorMessage);
    } finally {
      setIsEditing(false);
    }
  };

  // Handle delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setDeleteError(null);
    setIsDeleting(true);
    
    try {
      // Call the Redux action and await the result
      await deleteUser(selectedUser.id.toString());
      
      // Treat any resolved promise as success (DELETE may return empty body)
      setIsDeleteDialogOpen(false);
      setSelectedUser(null);
      setDeleteError(null);
      // Refresh the users list to show the updated data
      if (userMspInfo?.msp_id) {
        getUsersByMspId(userMspInfo.msp_id.toString());
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      
      // Extract the error message from the error object
      let errorMessage = 'Failed to delete user';
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      setDeleteError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Open edit dialog
  const openEditDialog = (user: any) => {
    setSelectedUser(user);
    setFormData({
      email: user.email || '',
      role: user.role || 'TECHNICIAN',
      psa_user_id: user.psa_user_id || '',
      entra_object_id: user.entra_object_id || '',
    });
    setIsEditDialogOpen(true);
  };

  // Open delete dialog
  const openDeleteDialog = (user: any) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  return (
      <div style={{ margin: "2rem auto" }}>
      {/* Add User Button - Only show for admins */}
      {mayManageUsers && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateDialogOpen(true)}
            disabled={userMspLoading || !userMspInfo?.msp_id}
            sx={{
              backgroundColor: '#705d4a',
              '&:hover': {
                backgroundColor: '#5a4a3a',
              },
              fontFamily: 'var(--theo-font-sans)',
              fontWeight: 600,
            }}
          >
            Add User
          </Button>
        </Box>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          border: "1px solid #E0E0E0",
          maxHeight: 'calc(100vh - 180px)',
          overflowY: "auto",
        }}
      >
        <Table stickyHeader sx={{ minWidth: 700, fontFamily: 'var(--theo-font-sans)' }}>
          <TableHead>
            <TableRow sx={{ background: "#F7F7F7" }}>
              <TableCell sx={{ textAlign: "center", fontFamily: "var(--theo-font-sans)", fontSize: "12px", fontWeight: 500, color: "#838383", cursor: "pointer" }}>Email</TableCell>
              <TableCell sx={{ textAlign: "center", fontFamily: "var(--theo-font-sans)", fontSize: "12px", fontWeight: 500, color: "#838383", cursor: "pointer" }}>Role</TableCell>
              <TableCell sx={{ textAlign: "center", fontFamily: "var(--theo-font-sans)", fontSize: "12px", fontWeight: 500, color: "#838383" }}>Teams</TableCell>
              {mayManageUsers && (
                <TableCell sx={{ textAlign: "center", fontFamily: "var(--theo-font-sans)", fontSize: "12px", fontWeight: 500, color: "#838383", cursor: "pointer" }}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {(userMspLoading || isLoading) && users.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={mayManageUsers ? 4 : 3}
                  sx={{
                    textAlign: 'center',
                    fontFamily: 'var(--theo-font-sans)',
                    color: '#6B7280',
                    py: 6,
                    fontSize: 16,
                    fontWeight: 500,
                    background: "#FAFAFA",
                    borderBottom: "none"
                  }}
                >
                  <CircularProgress size={20} sx={{ color: "#705d4a", mb: 1 }} />
                  <br />
                  Loading users...
                </TableCell>
              </TableRow>
            ) : (userMspError || error) && users.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={mayManageUsers ? 4 : 3}
                  sx={{
                    textAlign: 'center',
                    fontFamily: 'var(--theo-font-sans)',
                    color: '#d32f2f',
                    py: 6,
                    fontSize: 16,
                    fontWeight: 500,
                    background: "#FAFAFA",
                    borderBottom: "none"
                  }}
                >
                  {userMspError || error}
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={mayManageUsers ? 4 : 3}
                  sx={{
                    textAlign: 'center',
                    fontFamily: 'var(--theo-font-sans)',
                    color: '#6B7280',
                    py: 6,
                    fontSize: 16,
                    fontWeight: 500,
                    background: "#FAFAFA",
                    borderBottom: "none"
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 28, color: "#B0B4BA", mb: 1 }} />
                  <br />
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((user: any, idx: number) => {
                return (
                  <TableRow
                    key={user.id}
                    sx={{
                      background: idx % 2 === 0 ? "#FAFAFA" : "#FFF",
                      "&:hover": { background: "#F0F0F0" },
                      transition: "background 0.2s"
                    }}
                  >
                    <TableCell sx={{ py: 1.5, fontSize: 14, fontFamily: 'var(--theo-font-sans)', textAlign: 'center', cursor: 'pointer' }}>{user.email}</TableCell>
                    <TableCell sx={{ py: 1.5, fontSize: 14, fontFamily: 'var(--theo-font-sans)', textAlign: 'center', cursor: 'pointer' }}>{user.role}</TableCell>
                    <TableCell
                      title={teamsStatusHint(user.teams_status)}
                      sx={{ py: 1.5, fontSize: 14, fontFamily: 'var(--theo-font-sans)', textAlign: 'center', color: teamsStatusColor(user.teams_status), fontWeight: 600 }}
                    >
                      {teamsStatusLabel(user.teams_status)}
                    </TableCell>
                    {mayManageUsers && (
                      <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(user)}
                            sx={{
                              color: '#705d4a',
                              '&:hover': {
                                backgroundColor: 'rgba(112, 93, 74, 0.1)',
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => openDeleteDialog(user)}
                            sx={{
                              color: '#705d4a',
                              '&:hover': {
                                backgroundColor: 'rgba(112, 93, 74, 0.1)',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create User Dialog - Only for admins */}
      {mayManageUsers && (
        <Dialog open={isCreateDialogOpen} onClose={() => { setIsCreateDialogOpen(false); setCreateError(null); setFormData({ email: '', role: 'TECHNICIAN', psa_user_id: '', entra_object_id: '' }); setPsaUsers([]); }} maxWidth="sm" fullWidth sx={getDropdownSafeDialogSx()}>
          <DialogTitle sx={{ fontFamily: 'var(--theo-font-sans)', fontWeight: 600 }}>Add New User</DialogTitle>
          <DialogContent sx={{ overflow: "visible" }}>
            <Box sx={{ pt: 2 }}>
              {createError && (
                <Box sx={{ 
                  mb: 2, 
                  p: 2, 
                  backgroundColor: '#fff3cd', 
                  border: '1px solid #ffc107', 
                  borderRadius: 1,
                  color: '#856404',
                  fontFamily: 'var(--theo-font-sans)',
                  fontSize: 14,
                  fontWeight: 500
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 16 }}>⚠️</Typography>
                    <Box>
                      <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                        Email Already Exists
                      </Typography>
                      <Typography sx={{ fontSize: 13 }}>
                        {createError}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              {showPsaUserPicker && (
                <LayeredAutocompleteSelect
                  label="PSA User"
                  value={formData.psa_user_id}
                  onChange={(nextValue) => {
                    const selectedPsaUser = psaUsers.find((user) => user.id === nextValue);
                    if (selectedPsaUser) {
                      setFormData((prev) => ({
                        ...prev,
                        psa_user_id: nextValue,
                        email: selectedPsaUser.email || prev.email,
                      }));
                    } else {
                      setFormData((prev) => ({ ...prev, psa_user_id: nextValue }));
                    }
                    setCreateError(null);
                  }}
                  options={psaUsers.map((user) => ({
                    value: user.id,
                    label: user.email
                      ? `${user.name} (${user.email})`
                      : `${user.name} (${user.id})`,
                  }))}
                  loading={loadingPsaUsers}
                  noOptionsText={loadingPsaUsers ? "Loading PSA users..." : "No PSA users found"}
                  sx={{ mb: 2 }}
                  required
                />
              )}
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  setCreateError(null); // Clear error when user types
                }}
                sx={{ mb: 2 }}
                required
                error={!!createError}
                helperText={createError ? "Try a different email address that hasn't been used before" : "Enter the user's email address"}
              />
              <LayeredAutocompleteSelect
                label="Role"
                value={formData.role}
                onChange={(nextValue) =>
                  setFormData({ ...formData, role: nextValue as TheoMgmtRole })
                }
                options={roleSelectOptions}
                disableClearable
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => {
              setIsCreateDialogOpen(false);
              setCreateError(null);
              setFormData({ email: '', role: 'TECHNICIAN', psa_user_id: '', entra_object_id: '' });
              setPsaUsers([]);
              setIsCreating(false);
            }} sx={{ fontFamily: 'var(--theo-font-sans)' }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              variant="contained"
              disabled={!formData.email.trim() || (showPsaUserPicker && !formData.psa_user_id.trim()) || isCreating}
              sx={{
                backgroundColor: '#705d4a',
                '&:hover': { backgroundColor: '#5a4a3a' },
                '&:disabled': { backgroundColor: '#ccc' },
                fontFamily: 'var(--theo-font-sans)',
              }}
            >
              {isCreating ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit User Dialog - Only for admins */}
      {mayManageUsers && (
        <Dialog open={isEditDialogOpen} onClose={() => { setIsEditDialogOpen(false); setEditError(null); setSelectedUser(null); setFormData({ email: '', role: 'TECHNICIAN', psa_user_id: '', entra_object_id: '' }); }} maxWidth="sm" fullWidth sx={getDropdownSafeDialogSx()}>
          <DialogTitle sx={{ fontFamily: 'var(--theo-font-sans)', fontWeight: 600 }}>Edit User</DialogTitle>
          <DialogContent sx={{ overflow: "visible" }}>
            <Box sx={{ pt: 2 }}>
              {editError && (
                <Box sx={{ 
                  mb: 2, 
                  p: 2, 
                  backgroundColor: '#ffebee', 
                  border: '1px solid #f44336', 
                  borderRadius: 1,
                  color: '#d32f2f',
                  fontFamily: 'var(--theo-font-sans)',
                  fontSize: 14
                }}>
                  {editError}
                </Box>
              )}
              <TextField
                fullWidth
                label="Email"
                value={formData.email}
                disabled
                sx={{ mb: 2 }}
              />
              <LayeredAutocompleteSelect
                label="Role"
                value={formData.role}
                onChange={(nextValue) =>
                  setFormData({ ...formData, role: nextValue as TheoMgmtRole })
                }
                options={roleSelectOptions}
                disableClearable
              />
              <TextField
                fullWidth
                label="Entra object ID"
                value={formData.entra_object_id}
                onChange={(e) => {
                  setFormData({ ...formData, entra_object_id: e.target.value });
                  setEditError(null);
                }}
                sx={{ mt: 2 }}
                placeholder="570741ec-bec6-44a8-b7b8-5dcbe900012c"
                helperText="Use this when their Microsoft email differs from the Theo email. In Entra, open Users, select them, and copy Object ID. After saving, their next Teams message links to this user. They still send one message before notifications work. Leave blank to clear."
              />
              <Typography sx={{ mt: 1, fontFamily: 'var(--theo-font-sans)', fontSize: 13 }}>
                <Box
                  component="a"
                  href={ENTRA_USERS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: "#3C5BFF", fontWeight: 600 }}
                >
                  Open Entra users
                </Box>
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => {
              setIsEditDialogOpen(false);
              setEditError(null);
              setSelectedUser(null);
              setFormData({ email: '', role: 'TECHNICIAN', psa_user_id: '', entra_object_id: '' });
              setIsEditing(false);
            }} sx={{ fontFamily: 'var(--theo-font-sans)' }}>
              Cancel
            </Button>
            <Button
              onClick={handleEditUser}
              variant="contained"
              disabled={isEditing}
              sx={{
                backgroundColor: '#705d4a',
                '&:hover': { backgroundColor: '#5a4a3a' },
                '&:disabled': { backgroundColor: '#ccc' },
                fontFamily: 'var(--theo-font-sans)',
              }}
            >
              {isEditing ? 'Updating...' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog - Only for admins */}
      {mayManageUsers && (
        <Dialog open={isDeleteDialogOpen} onClose={() => { setIsDeleteDialogOpen(false); setDeleteError(null); setSelectedUser(null); setIsDeleting(false); }} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontFamily: 'var(--theo-font-sans)', fontWeight: 600 }}>Confirm Delete</DialogTitle>
          <DialogContent>
            {deleteError && (
              <Box sx={{ 
                mb: 2, 
                p: 2, 
                backgroundColor: '#ffebee', 
                border: '1px solid #f44336', 
                borderRadius: 1,
                color: '#d32f2f',
                fontFamily: 'var(--theo-font-sans)',
                fontSize: 14
              }}>
                {deleteError}
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ 
                backgroundColor: '#ffebee', 
                borderRadius: '50%', 
                width: 48, 
                height: 48, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: '#d32f2f'
              }}>
                <Typography sx={{ fontSize: 24 }}>⚠️</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontFamily: 'var(--theo-font-sans)', fontSize: 18, fontWeight: 600, mb: 1 }}>
                  Delete User
                </Typography>
                <Typography sx={{ fontFamily: 'var(--theo-font-sans)', fontSize: 14, color: '#666' }}>
                  Are you sure you want to delete <strong>{selectedUser?.email}</strong>? This action cannot be undone.
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => {
              setIsDeleteDialogOpen(false);
              setDeleteError(null);
              setSelectedUser(null);
              setIsDeleting(false);
            }} sx={{ fontFamily: 'var(--theo-font-sans)' }}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteUser}
              variant="contained"
              disabled={isDeleting}
              sx={{
                backgroundColor: '#d32f2f',
                '&:hover': { backgroundColor: '#b71c1c' },
                '&:disabled': { backgroundColor: '#ccc' },
                fontFamily: 'var(--theo-font-sans)',
                fontWeight: 600,
                px: 3
              }}
            >
              {isDeleting ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      </div>
  );
};

export default TheoUsers;
